import React, { useEffect, useMemo, useState } from "react";
import {
    Plus,
    Save,
    Sparkles,
    Trash2,
    Loader2,
    FileBarChart2,
    Wand2,
    RefreshCw,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { fmtEUR } from "@/lib/format";
import { generateCostBreakdown, saveCostItems, analyzeTender } from "@/lib/api";

const PROMPT_PRESETS = [
    "Genérame el presupuesto con un 6% de beneficio industrial y 13% de gastos generales.",
    "Aplica un 5% de baja sobre el presupuesto base, con beneficio industrial del 6%.",
    "Reparte los costes directos al 70% personal y 30% materiales.",
    "Incluye una partida de subcontratación del 15% del total.",
];

const CostRow = ({ item, idx, onUpdate, onRemove }) => {
    const subtotal =
        (Number(item.quantity) || 0) * (Number(item.unit_price) || 0);
    return (
        <tr data-testid={`cost-row-${idx}`} className="border-b border-ink-100">
            <td className="num text-ink-400 px-2">{idx + 1}</td>
            <td className="px-2">
                <select
                    value={item.cost_type || "direct"}
                    onChange={(e) => onUpdate(idx, "cost_type", e.target.value)}
                    className="h-8 rounded-xl border border-ink-200 text-xs font-bold px-2 w-full bg-white"
                    data-testid={`cost-type-${idx}`}
                >
                    <option value="direct">Directo</option>
                    <option value="indirect">Indirecto</option>
                </select>
            </td>
            <td className="px-2">
                <Input
                    value={item.category || ""}
                    onChange={(e) => onUpdate(idx, "category", e.target.value)}
                    className="h-8 rounded-xl border-ink-200 text-sm"
                    placeholder="Personal"
                    data-testid={`cost-category-${idx}`}
                />
            </td>
            <td className="px-2">
                <Input
                    value={item.description || ""}
                    onChange={(e) => onUpdate(idx, "description", e.target.value)}
                    className="h-8 rounded-xl border-ink-200 text-sm"
                    placeholder="Jefe de proyecto"
                    data-testid={`cost-description-${idx}`}
                />
            </td>
            <td className="px-2">
                <Input
                    value={item.unit || ""}
                    onChange={(e) => onUpdate(idx, "unit", e.target.value)}
                    className="h-8 rounded-xl border-ink-200 text-sm font-mono"
                    placeholder="ud"
                />
            </td>
            <td className="px-2">
                <Input
                    type="number"
                    step="0.01"
                    value={item.quantity}
                    onChange={(e) => onUpdate(idx, "quantity", e.target.value)}
                    className="h-8 rounded-xl border-ink-200 text-sm font-mono text-right"
                    data-testid={`cost-quantity-${idx}`}
                />
            </td>
            <td className="px-2">
                <Input
                    type="number"
                    step="0.01"
                    value={item.unit_price}
                    onChange={(e) => onUpdate(idx, "unit_price", e.target.value)}
                    className="h-8 rounded-xl border-ink-200 text-sm font-mono text-right"
                    data-testid={`cost-price-${idx}`}
                />
            </td>
            <td className="num font-medium text-right pr-3">{fmtEUR(subtotal)}</td>
            <td className="px-2">
                <button
                    onClick={() => onRemove(idx)}
                    className="text-ink-400 hover:text-red-600 transition-colors"
                    data-testid={`cost-delete-${idx}`}
                    title="Eliminar partida"
                >
                    <Trash2 size={14} />
                </button>
            </td>
        </tr>
    );
};

const PliegoBudgetBreakdown = ({ items, onReanalyze, reanalyzing }) => {
    const hasItems = Array.isArray(items) && items.length > 0;
    return (
        <div
            className="bg-white rounded-2xl border border-ink-100 p-6"
            data-testid="pliego-budget-breakdown"
        >
            <div className="flex items-start justify-between gap-3 mb-4">
                <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-brand-600 to-cyan-400 text-white flex items-center justify-center">
                        <FileBarChart2 size={15} />
                    </div>
                    <h3 className="font-display text-lg font-black">
                        Desglose del presupuesto en el pliego
                    </h3>
                </div>
                <Button
                    variant="outline"
                    size="sm"
                    onClick={onReanalyze}
                    disabled={reanalyzing}
                    className="rounded-xl border-ink-200 h-8 px-3 text-xs font-bold"
                    data-testid="reanalyze-budget-btn"
                    title="Re-analizar el pliego para extraer el desglose económico"
                >
                    {reanalyzing ? (
                        <>
                            <Loader2 size={13} className="mr-1.5 animate-spin" />
                            Analizando…
                        </>
                    ) : (
                        <>
                            <RefreshCw size={13} className="mr-1.5" />
                            Re-analizar pliego
                        </>
                    )}
                </Button>
            </div>
            {hasItems ? (
                <>
                    <p className="text-xs text-ink-500 font-medium mb-3">
                        Importes literales extraídos del pliego por la IA.
                    </p>
                    <div className="divide-y divide-ink-100">
                        {items.map((b, i) => (
                            <div
                                key={`${b.label || "concepto"}-${b.amount || ""}-${i}`}
                                className="flex items-center justify-between py-2.5 text-sm"
                            >
                                <div className="text-ink-700 font-medium">
                                    {b.label || "Concepto"}
                                </div>
                                <div className="font-mono font-bold text-ink-950">
                                    {b.amount || "—"}
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            ) : (
                <div
                    className="bg-amber-50 border border-amber-200 text-amber-800 text-sm font-medium px-4 py-3 rounded-xl"
                    data-testid="pliego-budget-breakdown-empty"
                >
                    No se encuentra desglose del presupuesto base en el pliego. Pulsa
                    "Re-analizar pliego" si el análisis es antiguo o has subido nuevos documentos.
                </div>
            )}
        </div>
    );
};

export const CostBreakdown = ({ tender, onChange }) => {
    const [items, setItems] = useState(tender.cost_items || []);
    const [margin, setMargin] = useState(0);
    const [instructions, setInstructions] = useState("");
    const [generating, setGenerating] = useState(false);
    const [saving, setSaving] = useState(false);
    const [reanalyzing, setReanalyzing] = useState(false);

    useEffect(() => {
        setItems(tender.cost_items || []);
    }, [tender.id, tender.cost_items]);

    const directs = items.filter((it) => (it.cost_type || "direct") === "direct");
    const indirects = items.filter((it) => it.cost_type === "indirect");

    const sumOf = (xs) =>
        xs.reduce(
            (s, it) =>
                s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0),
            0
        );
    const totalDirect = useMemo(() => sumOf(directs), [items]); // eslint-disable-line
    const totalIndirect = useMemo(() => sumOf(indirects), [items]); // eslint-disable-line
    const total = totalDirect + totalIndirect;
    const marginAmount = (total * margin) / 100;
    const finalPrice = total + marginAmount;

    const update = (idx, key, val) =>
        setItems((xs) =>
            xs.map((it, i) =>
                i === idx
                    ? {
                          ...it,
                          [key]:
                              key === "quantity" || key === "unit_price"
                                  ? val === ""
                                      ? ""
                                      : Number(val)
                                  : val,
                      }
                    : it
            )
        );

    const addRow = (cost_type = "direct") =>
        setItems((xs) => [
            ...xs,
            {
                id: crypto.randomUUID(),
                category: "",
                description: "",
                unit: "ud",
                quantity: 1,
                unit_price: 0,
                cost_type,
                notes: "",
            },
        ]);

    const removeRow = (idx) => setItems((xs) => xs.filter((_, i) => i !== idx));

    const generate = async () => {
        setGenerating(true);
        try {
            const updated = await generateCostBreakdown(tender.id, instructions);
            setItems(updated.cost_items || []);
            onChange?.(updated);
            toast.success("Escandallo generado por IA");
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "No se pudo generar el escandallo"
            );
        } finally {
            setGenerating(false);
        }
    };

    const save = async () => {
        setSaving(true);
        try {
            const payload = items.map((it) => ({
                id: it.id,
                category: it.category || "",
                description: it.description || "",
                unit: it.unit || "ud",
                quantity: Number(it.quantity) || 0,
                unit_price: Number(it.unit_price) || 0,
                cost_type: it.cost_type === "indirect" ? "indirect" : "direct",
                notes: it.notes || "",
            }));
            const updated = await saveCostItems(tender.id, payload);
            onChange?.(updated);
            toast.success("Escandallo guardado");
        } catch (e) {
            toast.error("Error al guardar");
        } finally {
            setSaving(false);
        }
    };

    const reanalyze = async () => {
        setReanalyzing(true);
        try {
            const updated = await analyzeTender(tender.id);
            onChange?.(updated);
            const found = (updated?.analysis?.budget_breakdown || []).length;
            toast.success(
                found > 0
                    ? `Pliego re-analizado · ${found} conceptos extraídos`
                    : "Pliego re-analizado · sin desglose económico encontrado"
            );
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "No se pudo re-analizar el pliego"
            );
        } finally {
            setReanalyzing(false);
        }
    };

    const pliegoBudget = tender?.analysis?.budget_breakdown || [];
    const sacredSubrog = tender?.analysis?.subrogated_personnel || [];
    const sacredBoQ = tender?.analysis?.bill_of_quantities || [];
    const hasSacredInputs = sacredSubrog.length > 0 || sacredBoQ.length > 0;

    return (
        <div className="space-y-6" data-testid="cost-breakdown">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <div>
                    <h2 className="font-display text-2xl font-black">Escandallo de costes</h2>
                    <p className="text-sm text-ink-500 mt-1 font-medium">
                        Desglosa los costes directos e indirectos. La IA puede generar una
                        propuesta basada en el pliego y tus instrucciones.
                    </p>
                </div>
                <Button
                    className="rounded-xl bg-brand-600 hover:bg-brand-700 text-white font-bold shadow-pop"
                    onClick={save}
                    disabled={saving}
                    data-testid="save-costs-btn"
                >
                    {saving ? (
                        "Guardando…"
                    ) : (
                        <>
                            <Save size={15} className="mr-1.5" />
                            Guardar
                        </>
                    )}
                </Button>
            </div>

            {!tender.analysis && (
                <div className="bg-amber-50 border border-amber-200 text-amber-800 p-3 rounded-xl text-sm font-medium">
                    Genera primero el análisis IA desde la pestaña "Resumen IA" para
                    habilitar la generación automática del escandallo.
                </div>
            )}

            {hasSacredInputs && (
                <div
                    className="bg-gradient-to-r from-amber-50 to-indigo-50 border-l-4 border-amber-400 rounded-xl p-4 text-sm"
                    data-testid="sacred-inputs-banner"
                >
                    <div className="font-display font-bold text-ink-950 mb-1.5 flex items-center gap-2">
                        <Sparkles size={14} className="text-amber-600" />
                        Datos sagrados detectados en el pliego
                    </div>
                    <ul className="text-ink-700 space-y-1 pl-1">
                        {sacredSubrog.length > 0 && (
                            <li>
                                · <strong>{sacredSubrog.length} trabajador(es) a subrogar</strong>{" "}
                                — la IA usará sus salarios como coste mínimo obligatorio.
                            </li>
                        )}
                        {sacredBoQ.length > 0 && (
                            <li>
                                · <strong>{sacredBoQ.length} partidas presupuestarias del pliego</strong>{" "}
                                — se reproducirán literalmente como costes directos.
                            </li>
                        )}
                    </ul>
                </div>
            )}

            {/* 1. Pliego budget breakdown */}
            <PliegoBudgetBreakdown
                items={pliegoBudget}
                onReanalyze={reanalyze}
                reanalyzing={reanalyzing}
            />

            {/* 2. AI instructions box */}
            <div
                className="bg-gradient-to-br from-brand-50 to-cyan-50 rounded-2xl border border-brand-200 p-6"
                data-testid="ai-instructions-box"
            >
                <div className="flex items-center gap-2 mb-3">
                    <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-brand-600 to-cyan-400 text-white flex items-center justify-center">
                        <Wand2 size={15} />
                    </div>
                    <h3 className="font-display text-lg font-black">
                        Instrucciones para la IA
                    </h3>
                </div>
                <p className="text-xs text-ink-500 font-medium mb-3">
                    Indícale cómo construir el presupuesto. Si lo dejas vacío usará valores
                    estándar (gastos generales 13%, beneficio industrial 6%).
                </p>
                <Textarea
                    value={instructions}
                    onChange={(e) => setInstructions(e.target.value)}
                    placeholder="Ej: Genérame el presupuesto con un 5% de beneficio industrial y 18% de gastos generales. Aplica una baja del 8% sobre el presupuesto base."
                    rows={3}
                    className="bg-white border-brand-200 rounded-xl text-sm resize-none focus-visible:ring-brand-500"
                    data-testid="ai-instructions-input"
                />
                <div className="flex flex-wrap gap-2 mt-3">
                    {PROMPT_PRESETS.map((p) => (
                        <button
                            key={p}
                            type="button"
                            onClick={() => setInstructions(p)}
                            className="text-[11px] font-bold bg-white border border-brand-200 text-brand-700 hover:bg-brand-50 px-2.5 py-1 rounded-full transition-colors"
                            data-testid={`preset-${p.slice(0, 20)}`}
                        >
                            {p}
                        </button>
                    ))}
                </div>
                <div className="flex justify-end mt-4">
                    <Button
                        onClick={generate}
                        disabled={generating || !tender.analysis}
                        className="rounded-xl bg-brand-600 hover:bg-brand-700 text-white font-bold shadow-pop"
                        data-testid="generate-costs-btn"
                    >
                        {generating ? (
                            <>
                                <Loader2 size={15} className="mr-1.5 animate-spin" />
                                Generando…
                            </>
                        ) : (
                            <>
                                <Sparkles size={15} className="mr-1.5" />
                                Generar con IA
                            </>
                        )}
                    </Button>
                </div>
            </div>

            {/* 3. Direct + Indirect tables */}
            <CostSection
                title="Costes directos"
                badge="Personal · Materiales · Subcontratación · Equipos"
                items={items}
                indices={directs.map((it) => items.indexOf(it))}
                emptyHint="Sin costes directos. Añade partidas o genera con IA."
                onUpdate={update}
                onRemove={removeRow}
                onAdd={() => addRow("direct")}
                total={totalDirect}
                addTestId="add-direct-row"
                color="emerald"
            />

            <CostSection
                title="Costes indirectos"
                badge="Gastos generales · Estructura · Beneficio industrial · Seguros"
                items={items}
                indices={indirects.map((it) => items.indexOf(it))}
                emptyHint="Sin costes indirectos. Suelen ser un porcentaje de los directos."
                onUpdate={update}
                onRemove={removeRow}
                onAdd={() => addRow("indirect")}
                total={totalIndirect}
                addTestId="add-indirect-row"
                color="amber"
            />

            {/* Totals */}
            <div className="grid md:grid-cols-4 gap-0 border border-ink-200 bg-white rounded-2xl overflow-hidden">
                <div className="p-5 border-r border-ink-100">
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400 mb-2">
                        Costes directos
                    </div>
                    <div className="font-mono text-xl font-bold tracking-tight">
                        {fmtEUR(totalDirect)}
                    </div>
                </div>
                <div className="p-5 border-r border-ink-100 border-t md:border-t-0">
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400 mb-2">
                        Costes indirectos
                    </div>
                    <div className="font-mono text-xl font-bold tracking-tight">
                        {fmtEUR(totalIndirect)}
                    </div>
                </div>
                <div className="p-5 border-r border-ink-100 border-t md:border-t-0">
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-ink-400 mb-2">
                        Margen (%)
                    </div>
                    <div className="flex items-center gap-2">
                        <Input
                            type="number"
                            value={margin}
                            onChange={(e) => setMargin(Number(e.target.value) || 0)}
                            className="w-20 rounded-xl border-ink-300 font-mono text-right h-9"
                            data-testid="margin-input"
                        />
                        <div className="font-mono text-xs text-ink-500">
                            +{fmtEUR(marginAmount)}
                        </div>
                    </div>
                </div>
                <div className="p-5 bg-gradient-to-br from-brand-600 to-cyan-500 text-white border-t md:border-t-0">
                    <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-cyan-100 mb-2">
                        Precio final oferta
                    </div>
                    <div
                        className="font-mono text-xl font-bold tracking-tight"
                        data-testid="final-price"
                    >
                        {fmtEUR(finalPrice)}
                    </div>
                </div>
            </div>
        </div>
    );
};

const CostSection = ({
    title,
    badge,
    items,
    indices,
    emptyHint,
    onUpdate,
    onRemove,
    onAdd,
    total,
    addTestId,
    color,
}) => {
    const accent =
        color === "emerald"
            ? "bg-emerald-50 text-emerald-700 border-emerald-200"
            : "bg-amber-50 text-amber-700 border-amber-200";
    return (
        <div className="bg-white border border-ink-100 rounded-2xl overflow-hidden">
            <div className="px-5 py-4 flex items-center justify-between border-b border-ink-100">
                <div>
                    <h3 className="font-display text-lg font-black">{title}</h3>
                    <div className={`inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border mt-1 ${accent}`}>
                        {badge}
                    </div>
                </div>
                <div className="text-right">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-ink-400">
                        Subtotal
                    </div>
                    <div className="font-mono text-lg font-bold">{fmtEUR(total)}</div>
                </div>
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="bg-ink-50/50 text-[10px] font-bold uppercase tracking-wider text-ink-500">
                        <tr>
                            <th className="w-10 px-2 py-2 text-left">#</th>
                            <th className="w-28 px-2 py-2 text-left">Tipo</th>
                            <th className="px-2 py-2 text-left">Categoría</th>
                            <th className="px-2 py-2 text-left">Descripción</th>
                            <th className="w-20 px-2 py-2 text-left">Unidad</th>
                            <th className="w-24 px-2 py-2 text-right">Cantidad</th>
                            <th className="w-28 px-2 py-2 text-right">Precio ud.</th>
                            <th className="w-28 px-2 py-2 text-right">Subtotal</th>
                            <th className="w-10 px-2 py-2"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {indices.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={9}
                                    className="text-center py-8 text-ink-400 text-sm font-medium"
                                >
                                    {emptyHint}
                                </td>
                            </tr>
                        ) : (
                            indices.map((idx) => (
                                <CostRow
                                    key={items[idx].id || idx}
                                    item={items[idx]}
                                    idx={idx}
                                    onUpdate={onUpdate}
                                    onRemove={onRemove}
                                />
                            ))
                        )}
                    </tbody>
                </table>
            </div>
            <div className="border-t border-ink-100 p-3">
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={onAdd}
                    className="rounded-xl text-ink-700"
                    data-testid={addTestId}
                >
                    <Plus size={14} className="mr-1" />
                    Añadir partida
                </Button>
            </div>
        </div>
    );
};

export default CostBreakdown;
